import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiMail, FiDownload, FiCopy, FiCheck, FiStar, FiHeart, FiFileText } = FiIcons;

const SummaryExport = ({ allData, onComplete }) => {
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  // Extract data from allData structure
  const formData = {
    vision: allData?.bookVision || {},
    structure: allData?.structure || {},
    scope: allData?.scope || {},
    chapter: allData?.kickstart || {}
  };

  const generateBookVisionSummary = () => {
    const { vision } = formData;
    if (!vision.title || !vision.bookType || !vision.audience || !vision.transformation) {
      return "Book vision not completed";
    }

    const verseText = vision.coreVerse ? `, rooted in ${vision.coreVerse}` : '';
    return `"${vision.title}" is a ${vision.bookType.toLowerCase()} book for ${vision.audience}, written to help them ${vision.transformation}${verseText}.`;
  };

  const generateStyledHTML = () => {
    const { vision, structure, scope, chapter } = formData;
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My Book Plan - Planning with Purpose</title>
    <style>
        body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 14pt;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
            background: white;
        }
        
        .header {
            text-align: center;
            border-bottom: 3px solid #059669;
            padding-bottom: 20px;
            margin-bottom: 40px;
        }
        
        .header h1 {
            font-size: 24pt;
            font-weight: bold;
            color: #059669;
            margin: 0 0 10px 0;
            font-family: Georgia, serif;
        }
        
        .header .subtitle {
            font-size: 16pt;
            color: #666;
            font-style: italic;
        }
        
        .section {
            margin-bottom: 40px;
            page-break-inside: avoid;
        }
        
        .section-title {
            font-size: 18pt;
            font-weight: bold;
            color: #059669;
            border-bottom: 2px solid #059669;
            padding-bottom: 8px;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
        }
        
        .section-icon {
            margin-right: 10px;
            font-size: 20pt;
        }
        
        .field {
            margin-bottom: 15px;
        }
        
        .field-label {
            font-weight: bold;
            color: #374151;
            margin-bottom: 5px;
        }
        
        .field-value {
            color: #4B5563;
            margin-left: 20px;
        }
        
        .highlight-box {
            background: linear-gradient(135deg, #D1FAE5 0%, #DBEAFE 100%);
            border: 2px solid #059669;
            border-radius: 10px;
            padding: 20px;
            margin: 20px 0;
        }
        
        .highlight-box .title {
            font-weight: bold;
            color: #059669;
            margin-bottom: 10px;
        }
        
        .highlight-box .content {
            font-style: italic;
            color: #065F46;
        }
        
        .chapter-item {
            background: #F9FAFB;
            border-left: 4px solid #059669;
            padding: 15px;
            margin: 10px 0;
        }
        
        .chapter-title {
            font-weight: bold;
            color: #374151;
            margin-bottom: 10px;
        }
        
        .chapter-detail {
            margin: 8px 0;
            margin-left: 15px;
        }
        
        .footer {
            text-align: center;
            border-top: 2px solid #059669;
            padding-top: 20px;
            margin-top: 40px;
            color: #666;
            font-style: italic;
        }
        
        .footer .scripture {
            font-weight: bold;
            color: #059669;
            margin-top: 10px;
        }
        
        @media print {
            body { 
                font-size: 12pt; 
                padding: 20px;
            }
            .section { 
                page-break-inside: avoid; 
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>MY BOOK PLAN</h1>
        <div class="subtitle">Planning with Purpose</div>
    </div>

    <div class="section">
        <div class="section-title">
            <span class="section-icon">📖</span>
            BOOK VISION
        </div>
        
        <div class="field">
            <div class="field-label">Title:</div>
            <div class="field-value">${vision.title || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Type:</div>
            <div class="field-value">${vision.bookType || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">God-Given Why:</div>
            <div class="field-value">${vision.why || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Target Audience:</div>
            <div class="field-value">${vision.audience || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Transformation Goal:</div>
            <div class="field-value">${vision.transformation || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Core Verse:</div>
            <div class="field-value">${vision.coreVerse || 'Not specified'}</div>
        </div>
        
        <div class="highlight-box">
            <div class="title">Book Vision Summary:</div>
            <div class="content">${generateBookVisionSummary()}</div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">
            <span class="section-icon">🏗️</span>
            STRUCTURE & OUTLINE
        </div>
        
        <div class="field">
            <div class="field-label">Structure Type:</div>
            <div class="field-value">${structure.structureType || 'Not specified'}</div>
        </div>
        
        ${structure.structureType === 'fiction' ? `
            <div class="field">
                <div class="field-label">Act 1 - Opening Hook:</div>
                <div class="field-value">${structure.act1 || 'Not specified'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">Act 2 - Midpoint Shift:</div>
                <div class="field-value">${structure.act2 || 'Not specified'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">Act 3 - Major Plot Point:</div>
                <div class="field-value">${structure.act3 || 'Not specified'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">Act 4 - Final Resolution:</div>
                <div class="field-value">${structure.act4 || 'Not specified'}</div>
            </div>
            
            ${structure.sceneIdeas && structure.sceneIdeas.length > 0 ? `
                <div class="field">
                    <div class="field-label">Scene Ideas:</div>
                    ${structure.sceneIdeas.map((scene, index) => 
                        scene.idea ? `<div class="field-value">${index + 1}. ${scene.idea}</div>` : ''
                    ).join('')}
                </div>
            ` : ''}
        ` : ''}
        
        ${structure.structureType === 'nonfiction' ? `
            <div class="field">
                <div class="field-label">Reader's Before:</div>
                <div class="field-value">${structure.readerBefore || 'Not specified'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">Reader's After:</div>
                <div class="field-value">${structure.readerAfter || 'Not specified'}</div>
            </div>
            
            ${structure.chapters && structure.chapters.length > 0 ? `
                <div class="field">
                    <div class="field-label">Chapters:</div>
                    ${structure.chapters.map((chapter, index) => `
                        <div class="chapter-item">
                            <div class="chapter-title">Chapter ${index + 1}: ${chapter.title || 'Untitled'}</div>
                            ${chapter.bigIdea ? `<div class="chapter-detail"><strong>Big Idea:</strong> ${chapter.bigIdea}</div>` : ''}
                            ${chapter.scripture ? `<div class="chapter-detail"><strong>Scripture:</strong> ${chapter.scripture}</div>` : ''}
                            ${chapter.takeaway ? `<div class="chapter-detail"><strong>Takeaway:</strong> ${chapter.takeaway}</div>` : ''}
                        </div>
                    `).join('')}
                </div>
            ` : ''}
        ` : ''}
    </div>

    <div class="section">
        <div class="section-title">
            <span class="section-icon">🎯</span>
            PROJECT SCOPE
        </div>
        
        <div class="field">
            <div class="field-label">Target Word Count:</div>
            <div class="field-value">${
                scope.targetWordCount === 'custom' 
                    ? `${parseInt(scope.customWordCount || 0).toLocaleString()} words (custom)`
                    : `${parseInt(scope.targetWordCount || 0).toLocaleString()} words`
            }</div>
        </div>
        
        <div class="field">
            <div class="field-label">Chapter Count:</div>
            <div class="field-value">${scope.chapterCount || 'Not specified'} chapters</div>
        </div>
        
        <div class="field">
            <div class="field-label">Timeline:</div>
            <div class="field-value">${
                scope.timeline === 'custom' 
                    ? `${scope.customTimeline} days (custom)`
                    : `${scope.timeline} days`
            }</div>
        </div>
        
        <div class="field">
            <div class="field-label">Writing Days per Week:</div>
            <div class="field-value">${scope.writingDaysPerWeek || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Writing Schedule:</div>
            <div class="field-value">${scope.writingSchedule || 'Not specified'}</div>
        </div>
        
        <div class="field">
            <div class="field-label">Obstacles to Address:</div>
            <div class="field-value">${scope.obstacles || 'Not specified'}</div>
        </div>
    </div>

    ${(chapter.startingIdea || chapter.firstParagraph || chapter.coreConnection) ? `
        <div class="section">
            <div class="section-title">
                <span class="section-icon">✍️</span>
                CHAPTER KICKSTART
            </div>
            
            <div class="field">
                <div class="field-label">Starting Idea:</div>
                <div class="field-value">${chapter.startingIdea || 'Not specified'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">First Paragraph:</div>
                <div class="field-value">${chapter.firstParagraph || 'Not written yet'}</div>
            </div>
            
            <div class="field">
                <div class="field-label">Core Message Connection:</div>
                <div class="field-value">${chapter.coreConnection || 'Not specified'}</div>
            </div>
        </div>
    ` : ''}

    <div class="footer">
        <div>Created with Planning with Purpose</div>
        <div>Plan your book with clarity, calling, and confidence.</div>
        <div class="scripture">"Write the vision; make it plain on tablets." - Habakkuk 2:2</div>
    </div>
</body>
</html>`;
  };

  const generateFullSummary = () => {
    const { vision, structure, scope, chapter } = formData;
    
    let summary = `MY BOOK PLAN - PLANNING WITH PURPOSE
==========================================

📖 BOOK VISION
--------------
Title: ${vision.title || 'Not specified'}
Type: ${vision.bookType || 'Not specified'}

God-Given Why: ${vision.why || 'Not specified'}

Target Audience: ${vision.audience || 'Not specified'}

Transformation Goal: ${vision.transformation || 'Not specified'}

Core Verse: ${vision.coreVerse || 'Not specified'}

Book Vision Summary: ${generateBookVisionSummary()}

`;

    // Structure section
    summary += `🏗️ STRUCTURE & OUTLINE
---------------------
`;

    if (structure.structureType === 'fiction') {
      summary += `Structure Type: Fiction

Act 1 - Opening Hook: ${structure.act1 || 'Not specified'}

Act 2 - Midpoint Shift: ${structure.act2 || 'Not specified'}

Act 3 - Major Plot Point: ${structure.act3 || 'Not specified'}

Act 4 - Final Resolution: ${structure.act4 || 'Not specified'}

`;
      
      if (structure.sceneIdeas && structure.sceneIdeas.length > 0) {
        summary += `Scene Ideas:\n`;
        structure.sceneIdeas.forEach((scene, index) => {
          if (scene.idea) {
            summary += `${index + 1}. ${scene.idea}\n`;
          }
        });
        summary += '\n';
      }
    } else if (structure.structureType === 'nonfiction') {
      summary += `Structure Type: Nonfiction

Reader's Before: ${structure.readerBefore || 'Not specified'}

Reader's After: ${structure.readerAfter || 'Not specified'}

`;
      
      if (structure.chapters && structure.chapters.length > 0) {
        summary += `Chapters:\n`;
        structure.chapters.forEach((chapter, index) => {
          summary += `Chapter ${index + 1}: ${chapter.title || 'Untitled'}\n`;
          if (chapter.bigIdea) summary += `  Big Idea: ${chapter.bigIdea}\n`;
          if (chapter.scripture) summary += `  Scripture: ${chapter.scripture}\n`;
          if (chapter.takeaway) summary += `  Takeaway: ${chapter.takeaway}\n`;
          summary += '\n';
        });
      }
    }

    // Scope section
    summary += `🎯 PROJECT SCOPE
---------------
Target Word Count: `;
    if (scope.targetWordCount === 'custom') {
      summary += `${parseInt(scope.customWordCount || 0).toLocaleString()} words (custom)\n`;
    } else {
      summary += `${parseInt(scope.targetWordCount || 0).toLocaleString()} words\n`;
    }

    summary += `Chapter Count: ${scope.chapterCount || 'Not specified'} chapters
Timeline: `;
    if (scope.timeline === 'custom') {
      summary += `${scope.customTimeline} days (custom)\n`;
    } else {
      summary += `${scope.timeline} days\n`;
    }

    summary += `Writing Days per Week: ${scope.writingDaysPerWeek || 'Not specified'}
Writing Schedule: ${scope.writingSchedule || 'Not specified'}
Obstacles to Address: ${scope.obstacles || 'Not specified'}

`;

    // Chapter Kickstart
    if (chapter.startingIdea || chapter.firstParagraph || chapter.coreConnection) {
      summary += `✍️ CHAPTER KICKSTART
-------------------
Starting Idea: ${chapter.startingIdea || 'Not specified'}

First Paragraph:
${chapter.firstParagraph || 'Not written yet'}

Core Message Connection: ${chapter.coreConnection || 'Not specified'}

`;
    }

    summary += `---
Created with Planning with Purpose
Plan your book with clarity, calling, and confidence.

"Write the vision; make it plain on tablets." - Habakkuk 2:2`;

    return summary;
  };

  const handleEmailSummary = () => {
    const subject = encodeURIComponent('My Complete Book Plan - Planning with Purpose');
    const body = encodeURIComponent(generateFullSummary());
    
    window.open(`mailto:?subject=${subject}&body=${body}`);
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateFullSummary());
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownloadStyled = () => {
    const htmlContent = generateStyledHTML();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.vision?.title || 'My Book Plan'} - Planning with Purpose.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadText = () => {
    const content = generateFullSummary();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${formData.vision?.title || 'My Book Plan'} - Planning with Purpose.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const completedSections = Object.keys(formData).filter(key => {
    const data = formData[key] || {};
    return Object.keys(data).length > 0;
  }).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl font-bold text-stone-800 mb-3">
          Summary & Export
        </h2>
        <p className="text-stone-600 text-lg">
          Celebrate your progress and take your plan with you!
        </p>
      </div>

      {/* Progress Celebration */}
      <motion.div 
        className="card bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200 mb-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="text-center">
          <SafeIcon icon={FiStar} className="text-emerald-600 text-4xl mx-auto mb-4" />
          <h3 className="font-serif text-2xl font-bold text-stone-800 mb-2">
            Congratulations!
          </h3>
          <p className="text-stone-600 text-lg mb-4">
            You've completed {completedSections} out of 4 planning sections.
          </p>
          <p className="text-emerald-700 font-medium">
            You're stewarding your God-given calling with excellence! 🌟
          </p>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Summary Content */}
        <div className="space-y-6">
          {/* Book Vision Summary */}
          {formData.vision && Object.keys(formData.vision).length > 0 && (
            <motion.div 
              className="card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiHeart} className="text-emerald-600 mr-2" />
                <h3 className="font-semibold text-stone-800">Book Vision</h3>
              </div>
              <div className="space-y-3 text-sm">
                <p><strong>Title:</strong> {formData.vision.title || 'Not specified'}</p>
                <p><strong>Type:</strong> {formData.vision.bookType || 'Not specified'}</p>
                {formData.vision.why && (
                  <p><strong>God-Given Why:</strong> {formData.vision.why.substring(0, 100)}...</p>
                )}
                {formData.vision.audience && (
                  <p><strong>Audience:</strong> {formData.vision.audience.substring(0, 100)}...</p>
                )}
                
                {/* Book Vision Summary */}
                <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
                  <p className="text-emerald-800 font-medium text-sm mb-2">Vision Summary:</p>
                  <p className="text-emerald-700 italic text-sm">
                    {generateBookVisionSummary()}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Structure Summary */}
          {formData.structure && Object.keys(formData.structure).length > 0 && (
            <motion.div 
              className="card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiStar} className="text-emerald-600 mr-2" />
                <h3 className="font-semibold text-stone-800">Structure & Outline</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p><strong>Type:</strong> {formData.structure.structureType || 'Not specified'}</p>
                {formData.structure.structureType === 'fiction' && formData.structure.act1 && (
                  <p><strong>Opening Hook:</strong> {formData.structure.act1.substring(0, 100)}...</p>
                )}
                {formData.structure.structureType === 'nonfiction' && formData.structure.chapters && (
                  <p><strong>Chapters:</strong> {formData.structure.chapters.length} planned</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Scope Summary */}
          {formData.scope && Object.keys(formData.scope).length > 0 && (
            <motion.div 
              className="card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiStar} className="text-emerald-600 mr-2" />
                <h3 className="font-semibold text-stone-800">Project Scope</h3>
              </div>
              <div className="space-y-2 text-sm">
                {formData.scope.targetWordCount && (
                  <p><strong>Target Length:</strong> {
                    formData.scope.targetWordCount === 'custom' 
                      ? `${parseInt(formData.scope.customWordCount || 0).toLocaleString()} words`
                      : `${parseInt(formData.scope.targetWordCount || 0).toLocaleString()} words`
                  }</p>
                )}
                {formData.scope.timeline && (
                  <p><strong>Timeline:</strong> {
                    formData.scope.timeline === 'custom' 
                      ? `${formData.scope.customTimeline} days`
                      : `${formData.scope.timeline} days`
                  }</p>
                )}
                <p><strong>Writing Days:</strong> {formData.scope.writingDaysPerWeek || 'Not specified'} days/week</p>
              </div>
            </motion.div>
          )}

          {/* Chapter Kickstart Summary */}
          {formData.chapter && Object.keys(formData.chapter).length > 0 && (
            <motion.div 
              className="card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiStar} className="text-emerald-600 mr-2" />
                <h3 className="font-semibold text-stone-800">Chapter Kickstart</h3>
              </div>
              <div className="space-y-2 text-sm">
                {formData.chapter.startingIdea && (
                  <p><strong>Starting Idea:</strong> {formData.chapter.startingIdea.substring(0, 100)}...</p>
                )}
                {formData.chapter.firstParagraph && (
                  <p><strong>First Paragraph:</strong> {formData.chapter.firstParagraph.split(' ').length} words written</p>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Export Actions */}
        <div className="lg:sticky lg:top-8">
          <motion.div 
            className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-serif text-xl font-bold text-stone-800 mb-6">
              Export Your Plan
            </h3>
            
            <div className="space-y-4">
              <button
                onClick={handleEmailSummary}
                className="btn-primary w-full flex items-center justify-center"
              >
                <SafeIcon icon={emailSent ? FiCheck : FiMail} className="mr-2" />
                {emailSent ? 'Email Prepared!' : 'Email to Myself'}
              </button>
              
              <button
                onClick={handleDownloadStyled}
                className="btn-secondary w-full flex items-center justify-center bg-gradient-to-r from-emerald-500 to-blue-500 text-white border-0 hover:from-emerald-600 hover:to-blue-600"
              >
                <SafeIcon icon={FiFileText} className="mr-2" />
                Download Styled Document
              </button>
              
              <button
                onClick={handleDownloadText}
                className="btn-secondary w-full flex items-center justify-center"
              >
                <SafeIcon icon={FiDownload} className="mr-2" />
                Download as Text File
              </button>
              
              <button
                onClick={handleCopyToClipboard}
                className="btn-secondary w-full flex items-center justify-center"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="mr-2" />
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
            
            <div className="mt-6 p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-amber-200">
              <p className="text-stone-600 text-sm leading-relaxed">
                💾 <strong>Auto-saved:</strong> All your progress is automatically saved in your browser. 
                You can return anytime to continue refining your plan.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed mt-2">
                📄 <strong>Styled Document:</strong> Opens in your browser - save as Word doc or print as PDF!
              </p>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div 
            className="card mt-6 bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h4 className="font-medium text-stone-800 mb-3">Your Next Steps</h4>
            <div className="space-y-3 text-sm text-stone-600">
              <div className="flex items-start">
                <span className="text-emerald-600 mr-2">1.</span>
                <p>Review your plan regularly and adjust as God leads</p>
              </div>
              <div className="flex items-start">
                <span className="text-emerald-600 mr-2">2.</span>
                <p>Set up your writing environment and remove obstacles</p>
              </div>
              <div className="flex items-start">
                <span className="text-emerald-600 mr-2">3.</span>
                <p>Begin writing with prayer and confidence in your calling</p>
              </div>
              <div className="flex items-start">
                <span className="text-emerald-600 mr-2">4.</span>
                <p>Trust God's timing and remain faithful to the process</p>
              </div>
            </div>
          </motion.div>

          {/* Encouragement */}
          <motion.div 
            className="card mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="font-medium text-stone-800 mb-3">Final Encouragement</h4>
            
            <p className="text-stone-600 text-sm leading-relaxed">
              God has equipped you for this journey. Your words will touch hearts, transform lives, 
              and advance His kingdom. Write with confidence in His calling upon your life.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default SummaryExport;